import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import { Iconify } from '@mgm/common/components';
import { useTranslation } from 'react-i18next';
import { commissionsActions } from '../../../store/commissions/slice';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getSelectedCommissionPlan } from '../../../store/commissions/selectors';
import { getTokenDetails } from '../../../store/authenticate/selectors';

const CommissionTypeView = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['commissions', 'pricing']);
  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);
  const tokenDetails = useAppSelector(getTokenDetails);

  const isDark = tokenDetails?.settings?.ui?.theme === 'dark';

  const commissionData = [
    {
      title: t('commissionSpecificProducts'),
      value: 'Products',
      description: t('compensateYourEmployeesForSelling'),
      illustration:
        '/product.png',
      illustrationDark:
        '/product.png',
      note: t('weWillCalculateOrder'),
      color: 'linear-gradient(135deg, #FFE0B3 0%, #FDE4B9 100%)',
    },
    {
      title: t('commissionEntireOrder'),
      value: 'Fixed',
      description: t('compensateYourEmployees'),
      illustration:
        '/order.png',
      illustrationDark:
        '/order.png',
      note: t('weWillCalculateOrderCommission'),
      color: 'linear-gradient(135deg, #BFEADF 0%, #A6D6C8 100%)',
    },
  ];

  const handleTypeSelected = (type: 'Fixed' | 'Steps' | 'Products') => {
    dispatch(commissionsActions.setSelectedCommissionsPlan({
      ...selectedCommissionPlan,
      type,
    }));
    dispatch(commissionsActions.setActiveStep(1));
  };

  return (
    <Container
      sx={{
        px: { xs: 1, lg: 2 },
      }}
      maxWidth={false}
    >
      <Box pt={4}>
        <Typography variant="h4" textAlign="center">{t('commissionEntireOrdersOrOnly')}</Typography>
        <Grid pt={8} container spacing={4}>
          {commissionData.map((item, i) => (
            <Grid
              key={i?.toString()}
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <Box
                component={Card}
                height={1}
                sx={{
                  background: item.color,
                }}
                boxShadow={0}
              >
                <CardMedia
                  component="img"
                  image={item.illustration}
                  alt={item.title}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: 350,
                    objectFit: 'contain',
                    padding: 2,
                    backgroundColor: 'transparent',
                  }}
                />
                <CardContent sx={{ padding: 4 }}>
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        ...(isDark && {
                          color: '#212B36',
                        }),
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography color={isDark ? '#454F5B' : 'text.secondary'}>
                      {item.description}
                    </Typography>
                    {item.note && (
                      <Box
                        display="flex"
                        alignItems="center"
                        mt={2}
                      >
                        <Iconify
                          icon="mdi:lightbulb-on-outline"
                          width={20}
                          sx={{
                            marginRight: 1,
                            ...(isDark && {
                              color: '#212B36',
                            }),
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            ...(isDark && {
                              color: '#212B36',
                            }),
                          }}
                        >
                          {item.note}
                        </Typography>
                      </Box>
                    )}
                    <Button
                      size="large"
                      onClick={() => handleTypeSelected(item.value as 'Fixed' | 'Steps' | 'Products')}
                      sx={{ marginTop: 2 }}
                      endIcon={(
                        <Box
                          component="svg"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          width={24}
                          height={24}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </Box>
                      )}
                    >
                      {t('chooseAndContinue')}
                    </Button>
                  </Box>
                </CardContent>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export {
  CommissionTypeView,
};
